import { useEffect, useMemo, useRef, useState } from "react";
import { Snackbar } from "@mui/material";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types.ts";
import NoTableEntries from "@components/DataTable/NoTableEntries.tsx";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import type { FilterItem } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import {
  buildFilterBodyForMovements,
  getMovementServicesFilterFields,
  MOVEMENT_SERVICES_FIELDS,
  movementStatuses,
} from "@features/movement-services/grid/utils.ts";
import { formatDateTime } from "@features/vessel-call/grid/utils.ts";
import { mapMovementServiceStatus } from "@features/vessel-call/vessel-call-status/MovementServices/preview/movementServiceStatus.mapper.ts";
import { useFormattedReference } from "@hooks/useFormattedReference.ts";
import type { MovementService } from "@services/movement-services/types.ts";
import { getMovements } from "@services/movementServicesApi.ts";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi.ts";
import type { FromToLocation } from "@services/vessel-call/types.ts";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { paths } from "@utils/navigation.tsx";
import { useTranslation } from "react-i18next";

export const LANG_BASE = "movement-services";
export const LANG_BASE_HEADER = `${LANG_BASE}.headerLabels`;
const TEST_ID_BASE = "movement-services-grid";

const LOCAL_STORAGE_PAGE_KEY = "movement-services-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "movement-services-page-size";
const LOCAL_STORAGE_FILTERS_KEY = "movement-services-filters";
const SEARCH_INQUIRY_ID = "movement-services.search";
const SEARCH_INQUIRY_CONTEXT = {
  module: "vessel_call",
  screen: "Movement Services",
  action: "search_movement_services",
  entityType: "movement_service",
};

interface MovementServicesData {
  items: MovementService[];
  totalElements: number;
}

export const MovementServices = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<FilterItem[]>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_FILTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 25;
  });
  const vesselCallService = "bff-vessel-call";

  const filtersBody = buildFilterBodyForMovements(filters);

  const { options: fromToLocations = [] } = useFormattedReference<FromToLocation>(
    REFERENCE_DATA_TYPES.FROM_TO_LOCATIONS,
    "bff-vessel-call",
  );

  const fromToLocationOptions = useMemo(
    () =>
      fromToLocations.map(({ fromToLocationCode }) => ({
        value: fromToLocationCode,
        label: fromToLocationCode,
      })),
    [fromToLocations],
  );

  const statusOptions = useMemo(
    () =>
      movementStatuses.map(({ value, label }) => ({
        value,
        label: t(`vessel-call.${LANG_BASE}.status.${label}`),
      })),
    [t],
  );

  const { data, isLoading, isFetching, isError, isSuccess, dataUpdatedAt, error } =
    useQuery<MovementServicesData>({
      queryKey: ["getMovementServices", page, pageSize, filters],
      queryFn: () => {
        return getMovements({
          service: vesselCallService,
          page: page - 1,
          pageSize,
          filters: filtersBody,
        });
      },
      staleTime: 0,
      refetchOnMount: true,
    });
  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching,
    isLoading,
    isError,
    isSuccess,
    error,
    dataUpdatedAt,
  });

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  useEffect(() => {
    if (isLoading || error || hasTrackedInitialLoad.current) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    completeScreenNavigation("Movement Services");
    trackScreenLoad("Vessel Call Management", "Movement Services", startedAt.current);
  }, [error, isLoading]);

  const movements = data?.items || [];
  const totalRecordCount = data?.totalElements || 0;

  const headers: Header[] = useMemo(
    () =>
      Object.values(MOVEMENT_SERVICES_FIELDS).map((field) => ({
        field,
        columnName: t(`${LANG_BASE_HEADER}.${field}`),
      })),
    [t],
  );

  const filterFields = useMemo(
    () =>
      getMovementServicesFilterFields({
        t,
        fromToLocationOptions,
        statusOptions,
      }),
    [fromToLocationOptions, statusOptions, t],
  );

  const onApplyFilters = (filters: FilterItem[]) => {
    startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    setFilters(filters);
    localStorage.setItem(LOCAL_STORAGE_FILTERS_KEY, JSON.stringify(filters));
    setPage(1);
    localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, "1");
  };

  const onClearFilters = () => {
    if (filters.length) {
      startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
    }
    setFilters([]);
    localStorage.removeItem(LOCAL_STORAGE_FILTERS_KEY);
  };

  if (isLoading) {
    return <LoadingOverlay loading={isLoading} />;
  }

  const mappedRows = movements.map((row) => {
    const statusMapping = mapMovementServiceStatus(String(row.status).toUpperCase());

    return {
      ...row,
      eta: row.eta ? formatDateTime(String(row.eta), "-") : "-",
      rtd: row.rtd ? formatDateTime(String(row.rtd), "-") : "-",
      ets: row.ets ? formatDateTime(String(row.ets), "-") : "-",
      ats: row.ats ? formatDateTime(String(row.ats), "-") : "-",
      etc: row.etc ? formatDateTime(String(row.etc), "-") : "-",
      atc: row.atc ? formatDateTime(String(row.atc), "-") : "-",
      badgeType: statusMapping.badgeType,
      status: t(statusMapping.translationKey, {
        defaultValue: row.status,
      }),
    };
  });

  return (
    <>
      {error && <Snackbar open autoHideDuration={6000} message={error.message} />}
      {movements.length || filters.length ? (
        <div data-testid={TEST_ID_BASE}>
          <CustomDataTable
            testId={TEST_ID_BASE}
            title={t(`${LANG_BASE}.title`)}
            actionName={t(`${LANG_BASE}.addVesselCall`)}
            headers={headers}
            rows={mappedRows}
            page={page}
            pageSize={pageSize}
            totalCount={totalRecordCount}
            canPreview={true}
            onPageChange={(newPage, newSize) => {
              if (newPage !== page || newSize !== pageSize) {
                startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
              }
              setPage(newPage);
              setPageSize(newSize);
              localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
              localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
            }}
            interModulesNavigation={{
              pathToModule: `/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}`,
              targetModuleResourceBy: "vesselCallId",
              pathToSubModule: `${paths.vesselCall.movementServices}`,
              targetSubModuleResourceBy: "movementId",
            }}
            navigationLinkBase=""
            navigationParam="movementId"
            pageSizeOptions={[5, 10, 15, 20, 25]}
            filters={filters}
            onApplyFilters={onApplyFilters}
            onClearFilters={onClearFilters}
            availableFields={filterFields}
          />
        </div>
      ) : (
        <NoTableEntries
          noMargin
          customMessagePrefix={`${LANG_BASE}.empty-table-title`}
          dataName={t("vessel-call.vessel-status.movement-services")}
          actionName={""}
          descriptionComponents={{ strong: <strong /> }}
        />
      )}
    </>
  );
};

export default MovementServices;
